import React from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY_INFO } from '../mock';

const FAQ_ITEMS_UNFALLGUTACHTEN = [
  {
    question: 'Was kostet ein Unfallgutachten?',
    answer: 'Die Kosten für ein Unfallgutachten variieren je nach Schadensumfang und Fahrzeugtyp. Bei unverschuldetem Unfall trägt in der Regel die gegnerische Haftpflichtversicherung die Kosten – je nach Einzelfall.'
  },
  {
    question: 'Wer bezahlt das Gutachten bei unverschuldetem Unfall?',
    answer: 'In den meisten Fällen übernimmt die Haftpflichtversicherung des Unfallverursachers die Gutachterkosten. Als Geschädigter haben Sie das Recht auf freie Gutachterwahl und müssen meist nicht in Vorleistung gehen.'
  },
  {
    question: 'Wie schnell bekomme ich einen Termin?',
    answer: 'Wir vereinbaren meist innerhalb von 24–48 Stunden einen Termin vor Ort. Die Begutachtung dauert etwa 30–60 Minuten. Das fertige Gutachten erhalten Sie in der Regel innerhalb von 2–3 Werktagen.'
  },
  {
    question: 'Kommt ihr wirklich bundesweit?',
    answer: 'Ja, wir sind deutschlandweit im Einsatz – egal ob Hamburg, NRW, Bayern oder Berlin. Sie müssen nicht zu uns fahren, wir kommen zu Ihnen.'
  },
  {
    question: 'Muss ich zu euch kommen?',
    answer: 'Nein, wir kommen direkt zu Ihnen vor Ort – bundesweit. Sie müssen nicht zu uns fahren. Wir vereinbaren einen Termin an einem für Sie passenden Ort.'
  },
  {
    question: 'Was ist der Unterschied zwischen Gutachten und Kostenvoranschlag?',
    answer: 'Ein Gutachten ist eine umfassende, rechtlich belastbare Schadensdokumentation durch einen unabhängigen Sachverständigen. Ein Kostenvoranschlag ist eine oberflächliche Schätzung durch eine Werkstatt. Ab ca. 750€ Schadensumfang ist ein Gutachten empfehlenswert.'
  },
  {
    question: 'Was passiert, wenn das Auto nicht fahrbereit ist?',
    answer: 'Kein Problem. Wir begutachten das Fahrzeug dort, wo es sich befindet – bei Ihnen zu Hause, in einer Werkstatt oder an einem anderen Standort. Teilen Sie uns einfach mit, wo sich das Fahrzeug befindet.'
  },
  {
    question: 'Welche Unterlagen benötigt ihr?',
    answer: 'Idealerweise: Fahrzeugschein/-brief, Unfallbericht (falls vorhanden), Fotos vom Unfallort und Kontaktdaten der Unfallbeteiligten. Aber keine Sorge – wir klären das im Gespräch und helfen Ihnen weiter.'
  },
  {
    question: 'Kann ich das Gutachten auch für meine Versicherung nutzen?',
    answer: 'Ja, das Gutachten ist eine objektive Dokumentation und kann bei jeder Versicherung zur Schadenregulierung eingereicht werden. Es dient als Beweismittel und Grundlage für die Schadensabwicklung.'
  },
  {
    question: 'Was ist mit Wertminderung und Nutzungsausfall?',
    answer: 'Beides wird im Gutachten berücksichtigt. Die Wertminderung beschreibt den Wertverlust des Fahrzeugs durch den Unfall. Der Nutzungsausfall entschädigt Sie für die Zeit, in der Sie Ihr Fahrzeug nicht nutzen konnten.'
  },
  {
    question: 'Wie lange dauert die Begutachtung?',
    answer: 'Die Vor-Ort-Begutachtung dauert in der Regel 30–60 Minuten. Das fertige, schriftliche Gutachten erhalten Sie dann innerhalb von 2–3 Werktagen.'
  },
  {
    question: 'Was passiert nach der Begutachtung?',
    answer: 'Nach der Begutachtung erstellen wir das schriftliche Gutachten und senden es Ihnen zu. Sie können es dann bei der Versicherung einreichen. Bei Fragen zur weiteren Schadenregulierung unterstützen wir Sie gerne.'
  }
];

export const SEOHeadUnfallgutachten = () => {
  const schemaBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Startseite",
        "item": "https://www.dein-gutachten-direkt.de/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Unfallgutachten",
        "item": "https://www.dein-gutachten-direkt.de/unfallgutachten"
      }
    ]
  };

  const schemaService = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Unfallgutachten",
    "provider": {
      "@type": "LocalBusiness",
      "name": COMPANY_INFO.name,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": COMPANY_INFO.address.street,
        "addressLocality": COMPANY_INFO.address.city,
        "postalCode": COMPANY_INFO.address.zip,
        "addressRegion": COMPANY_INFO.address.state,
        "addressCountry": "DE"
      },
      "telephone": "+494101847116"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Deutschland"
    },
    "description": "Unabhängige Unfallgutachten bundesweit direkt vor Ort. Professionelle Schadensbegutachtung nach Verkehrsunfällen durch qualifizierte Sachverständige.",
    "offers": {
      "@type": "Offer",
      "description": "Bei unverschuldetem Unfall werden die Kosten in der Regel von der gegnerischen Haftpflichtversicherung übernommen."
    }
  };

  const schemaFAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_ITEMS_UNFALLGUTACHTEN.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>Unfallgutachten bundesweit | Unabhängig & vor Ort | Dein Gutachten Direkt</title>
      <meta name="title" content="Unfallgutachten bundesweit | Unabhängig & vor Ort | Dein Gutachten Direkt" />
      <meta name="description" content="Professionelles Unfallgutachten direkt vor Ort – bundesweit. Unabhängige Schadensbegutachtung durch qualifizierte Sachverständige." />
      <meta name="keywords" content="Unfallgutachten, Kfz-Gutachten, Schadensgutachten, Gutachter nach Unfall, unabhängiger Gutachter, Rellingen, bundesweit, Wertminderung, Nutzungsausfall" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.dein-gutachten-direkt.de/unfallgutachten" />
      <meta property="og:title" content="Unfallgutachten bundesweit | Unabhängig & vor Ort" />
      <meta property="og:description" content="Professionelles Unfallgutachten direkt vor Ort – bundesweit. Unabhängige Schadensbegutachtung durch qualifizierte Sachverständige." />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content="https://www.dein-gutachten-direkt.de/unfallgutachten" />
      <meta property="twitter:title" content="Unfallgutachten bundesweit | Unabhängig & vor Ort" />
      <meta property="twitter:description" content="Professionelles Unfallgutachten direkt vor Ort – bundesweit" />

      {/* Canonical */}
      <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten" />

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify(schemaBreadcrumb)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaService)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaFAQ)}
      </script>
    </Helmet>
  );
};